import React from "react";
import Header from "../component/Header";
import Footer from "../component/Footer";
import { motion } from "framer-motion";

import EmailIcon from "@mui/icons-material/Email";
import PhoneIcon from "@mui/icons-material/Phone";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import BusinessIcon from "@mui/icons-material/Business";
import BadgeIcon from "@mui/icons-material/Badge";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import SupportAgentIcon from "@mui/icons-material/SupportAgent";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";

export default function Grievances() {
  return (
    <>
      <Header />

      {/* =====================================================
          HERO SECTION
      ====================================================== */}
      <section className="relative h-[300px] flex items-center justify-center bg-gradient-to-br from-[#0b1f3a] to-[#0a2540] overflow-hidden">

        {/* Background Glow */}
        <div className="absolute w-[400px] h-[400px] bg-blue-500/20 rounded-full blur-3xl animate-pulse top-[-100px] left-[-100px]" />

        <div className="absolute w-[300px] h-[300px] bg-orange-400/20 rounded-full blur-3xl animate-pulse bottom-[-80px] right-[-80px]" />

        {/* Hero Content */}
        <motion.div
          initial={{ opacity: 0, y: 40 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="relative z-10 px-6 text-center"
        >
          {/* Breadcrumb */}
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.2 }}
            className="text-gray-300 mb-3 text-sm md:text-base"
          >
            Home &nbsp; / &nbsp; Grievances
          </motion.p>

          {/* Title */}
          <motion.h1
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ delay: 0.3 }}
            className="text-4xl md:text-5xl font-bold text-white"
          >
            Grievance{" "}
            <span className="text-orange-400">
              Redressal Mechanism
            </span>
          </motion.h1>

          {/* Subtitle */}
          <motion.p
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.5 }}
            className="mt-4 text-gray-200 text-sm md:text-base max-w-2xl mx-auto"
          >
            We are committed to providing a transparent and accessible
            mechanism for addressing customer grievances and concerns.
          </motion.p>
        </motion.div>
      </section>

      {/* =====================================================
          MAIN CONTENT
      ====================================================== */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">

        <div className="max-w-7xl mx-auto">

          {/* =================================================
              INTRODUCTION
          ================================================== */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold text-[#0b1f3a] mb-5">
              Customer Grievances Redressal Mechanism
            </h2>

            <p className="text-gray-600 leading-7 max-w-5xl">
              The company committed to providing
              transparent and accessible customer support. Customers can
              contact us through the grievance redressal channels provided
              below for any concerns, complaints or service-related issues.
            </p>
          </motion.div>

          {/* =================================================
              COMPANY DETAILS
          ================================================== */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 mt-10">

            {/* Company Name */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-orange-100 mb-4">
                <BusinessIcon className="text-orange-500" />
              </div>

              <h3 className="font-bold text-xl text-[#0b1f3a] mb-2">
                Company Name
              </h3>

              <p className="text-gray-600 leading-6">
                Kisga Leasing and Finance Pvt Ltd
              </p>
            </motion.div>

            {/* CIN */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1, duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-orange-100 mb-4">
                <BadgeIcon className="text-orange-500" />
              </div>

              <h3 className="font-bold text-xl text-[#0b1f3a] mb-2">
                CIN
              </h3>

              <p className="text-gray-600">
                U74899DL1993PTC053939
              </p>
            </motion.div>

            {/* GSTIN */}
            <motion.div
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2, duration: 0.5 }}
              viewport={{ once: true }}
              className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all duration-300"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-orange-100 mb-4">
                <ReceiptLongIcon className="text-orange-500" />
              </div>

              <h3 className="font-bold text-xl text-[#0b1f3a] mb-2">
                GSTIN
              </h3>

              <p className="text-gray-600">
                07AAACK9423G1ZL
              </p>
            </motion.div>

          </div>

          {/* =================================================
              REGISTERED ADDRESS
          ================================================== */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="mt-6 bg-white border border-gray-200 rounded-2xl p-6 md:p-8 shadow-md"
          >
            <div className="flex flex-col md:flex-row gap-5">

              <div className="w-12 h-12 shrink-0 flex items-center justify-center rounded-xl bg-orange-100">
                <LocationOnIcon className="text-orange-500" />
              </div>

              <div>
                <h3 className="text-xl font-bold text-[#0b1f3a] mb-2">
                  Registered Address
                </h3>

                <p className="text-gray-600 leading-7">
                  B-7, 
                  <br />
                  New Multan Nagar, Paschim Vihar,
                  <br />
                  New Delhi - 110056
                 
                </p>
              </div>

            </div>
          </motion.div>

          {/* =================================================
              GRIEVANCE CONTACT
          ================================================== */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="mt-16"
          >

            <h2 className="text-3xl font-bold text-[#0b1f3a] mb-3">
              Grievance Redressal Officer / Customer Support
            </h2>

            <p className="text-gray-600 mb-8">
              For any complaint, grievance or service-related concern,
              customers may contact the designated grievance contact person
              using the details below.
            </p>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">

              {/* Contact Person */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">

                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mb-4">
                  <SupportAgentIcon className="text-orange-500" />
                </div>

                <p className="text-sm text-gray-500 mb-1">
                  Contact Person
                </p>

                <h3 className="text-xl font-bold text-[#0b1f3a]">
                 Sangeeta Chauhan
                </h3>

              </div>

              {/* Email */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">

                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mb-4">
                  <EmailIcon className="text-orange-500" />
                </div>

                <p className="text-sm text-gray-500 mb-1">
                  Email ID
                </p>

                <a
                  href="mailto:grievance@b4salary.com"
                  className="text-lg font-semibold text-[#0b1f3a] hover:text-orange-500 break-all transition"
                >
                  grievance@b4salary.com
                </a>

              </div>

              {/* Phone */}
              <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-md hover:shadow-xl transition-all">

                <div className="w-12 h-12 rounded-xl bg-orange-100 flex items-center justify-center mb-4">
                  <PhoneIcon className="text-orange-500" />
                </div>

                <p className="text-sm text-gray-500 mb-1">
                  Contact Number
                </p>

                <a
                  href="tel:+91+8796553277"
                  className="text-xl font-bold text-[#0b1f3a] hover:text-orange-500 transition"
                >
                  +91 8796553277
                </a>

              </div>

            </div>
          </motion.div>
           {/* =================================================
              CONTACT CTA
          ================================================== */}
           <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
            className="text-center mt-14"
          >

            <h2 className="text-2xl md:text-3xl font-bold text-[#0b1f3a]">
              Need Help With a Grievance?
            </h2>

            <p className="text-gray-600 mt-3 mb-6">
              Our grievance support team is available to assist you.
            </p>

            <a
              href="https://b4salary.com/contact-us"
              className="inline-flex items-center gap-2 bg-orange-500 hover:bg-orange-600 text-white px-7 py-3 rounded-full shadow-md hover:scale-105 transition-all duration-300"
            >
             
              Contact Grievance Team
            </a> 

           </motion.div>

          {/* =================================================
              HOW TO REGISTER A GRIEVANCE
          ================================================== */}
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="mt-16"
          >

            <h2 className="text-3xl font-bold text-[#0b1f3a] mb-3">
              How to Register a Grievance
            </h2>

            <p className="text-gray-600 mb-8">
              Customers can follow the steps below to submit their
              complaint or grievance.
            </p>

            <div className="space-y-5">

              {/* STEP 1 */}
              <div className="flex gap-5 p-6 bg-gray-50 rounded-2xl border border-gray-200">

                <div className="w-10 h-10 shrink-0 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
                  1
                </div>

                <div>
                  <h3 className="text-lg font-bold text-[#0b1f3a] mb-2">
                    Submit Your Complaint
                  </h3>

                  <p className="text-gray-600 leading-7">
                    Customers may submit their complaint or grievance by
                    sending an email to{" "}
                    <a
                      href="mailto:grievance@b4salary.com"
                      className="text-orange-500 font-semibold hover:underline"
                    >
                      grievance@b4salary.com
                    </a>
                    .
                  </p>
                </div>

              </div>

              {/* STEP 2 */}
              <div className="flex gap-5 p-6 bg-gray-50 rounded-2xl border border-gray-200">

                <div className="w-10 h-10 shrink-0 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
                  2
                </div>

                <div>
                  <h3 className="text-lg font-bold text-[#0b1f3a] mb-2">
                    Provide Relevant Details
                  </h3>

                  <p className="text-gray-600 leading-7">
                    Please provide sufficient details regarding your
                    complaint, including relevant application or reference
                    details, so that the matter can be examined appropriately.
                  </p>
                </div>

              </div>

              {/* STEP 3 */}
              <div className="flex gap-5 p-6 bg-gray-50 rounded-2xl border border-gray-200">

                <div className="w-10 h-10 shrink-0 rounded-full bg-orange-500 text-white flex items-center justify-center font-bold">
                  3
                </div>

                <div>
                  <h3 className="text-lg font-bold text-[#0b1f3a] mb-2">
                    Review and Resolution
                  </h3>

                  <p className="text-gray-600 leading-7">
                    The grievance will be reviewed by the concerned team and
                    an appropriate response will be communicated to the
                    customer through the available communication channels.
                  </p>
                </div>

              </div>

            </div>
          </motion.div>

          {/* =================================================
              RESOLUTION & RBI OMBUDSMAN INFORMATION
          ================================================== */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="mt-10"
          >

            <div className="space-y-4 text-gray-600 leading-7">

              {/* 7 Working Days */}
              <p>
                After examining the matter, customer grievance/complaint
                shall be resolved within{" "}
                <strong className="text-[#0b1f3a]">
                  7 working days
                </strong>.
              </p>

              {/* One Month / RBI Ombudsman */}
              <p>
                If the complaint/grievance/dispute is not redressed within a
                period of one month after the date of representation or
                complaint to the Company, the borrower/customer may approach
                the Ombudsman of RBI (not later than one year after the reply
                received from the Company).
              </p>

              {/* RBI Ombudsman Link */}
              <p>
                <strong className="text-[#0b1f3a]">
                  Details of RBI Ombudsman available on:
                </strong>{" "}

                <a
                  href="https://rbi.org.in/Scripts/AboutUsDisplay.aspx?pg=BankingOmbudsmen.html"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-orange-500 font-semibold hover:text-orange-600 hover:underline"
                >
                  RBI Ombudsman
                </a>
              </p>

            </div>

          </motion.div>

          {/* =================================================
              OUR COMMITMENT
          ================================================== */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7 }}
            viewport={{ once: true }}
            className="mt-16 bg-gradient-to-r from-[#0b1f3a] to-[#0a2540] rounded-3xl p-8 md:p-12 text-white"
          >

            <div className="max-w-4xl">

              <h2 className="text-3xl font-bold mb-5">
                Our Commitment
              </h2>

              <p className="text-gray-300 leading-7 mb-6">
                Capora Fintech Private Limited strives to ensure that
                customer concerns are handled fairly, transparently and
                efficiently. We encourage customers to contact us whenever
                they have a concern relating to our services.
              </p>

              <div className="space-y-3">

                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="text-orange-400" />

                  <span>
                    Transparent grievance handling
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="text-orange-400" />

                  <span>
                    Dedicated grievance contact
                  </span>
                </div>

                <div className="flex items-center gap-3">
                  <CheckCircleIcon className="text-orange-400" />

                  <span>
                    Customer-focused resolution process
                  </span>
                </div>

              </div>

            </div>

          </motion.div>

        </div>
      </section>
      

      {/* =====================================================
          FOOTER
      ====================================================== */}
      <Footer />
    </>
  );
}